import { NavLink } from 'react-router-dom';
import { type ReactNode } from 'react';
import { useDueCount } from '../../hooks/useDueCount';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  {
    to: '/',
    label: 'Learn',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    to: '/read',
    label: 'Read',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    to: '/review',
    label: 'Review',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    to: '/progress',
    label: 'Progress',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

export function BottomNav() {
  const { dueCount } = useDueCount();

  return (
    <nav className="
      fixed z-50 bg-white border-[var(--color-sand-200)]
      bottom-0 left-0 right-0 border-t shadow-[0_-4px_20px_rgba(44,24,16,0.08)]
      md:top-0 md:bottom-0 md:right-auto md:w-[72px] md:border-t-0 md:border-r md:shadow-[4px_0_20px_rgba(44,24,16,0.06)] md:flex md:flex-col
    ">
      {/* Logo — visible only in sidebar mode */}
      <div className="hidden md:flex items-center justify-center h-16 border-b border-[var(--color-sand-200)]">
        <div className="w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-primary)] flex items-center justify-center shadow-[var(--shadow-sm)]">
          <span className="text-white font-arabic text-base">م</span>
        </div>
      </div>

      <div className="
        max-w-2xl mx-auto px-4 flex items-center justify-around h-16
        md:max-w-none md:px-0 md:flex-col md:flex-1 md:justify-center md:items-stretch md:gap-1
      ">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `
              relative flex flex-col items-center justify-center
              min-w-[56px] py-2 px-2
              rounded-[var(--radius-md)]
              transition-all duration-200
              md:mx-2 md:py-3 md:px-0 md:min-w-0
              ${isActive
                ? 'text-[var(--color-primary)]'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-sand-100)]'
              }
            `}
          >
            {({ isActive }) => (
              <>
                <div className={`
                  relative
                  transition-transform duration-200
                  ${isActive ? 'scale-110' : 'scale-100'}
                `}>
                  {item.icon}
                  {/* Badge count for due reviews on Review tab */}
                  {item.to === '/review' && dueCount > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 bg-[var(--color-error)] rounded-full flex items-center justify-center border-2 border-white">
                      <span className="text-[10px] font-bold text-white leading-none">{dueCount > 99 ? '99+' : dueCount}</span>
                    </span>
                  )}
                </div>
                <span className={`
                  text-xs mt-1 font-medium
                  ${isActive ? 'opacity-100' : 'opacity-70'}
                `}>
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute -bottom-0 w-8 h-0.5 bg-[var(--color-primary)] rounded-full" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>

      {/* Safe area padding for iOS — phone only */}
      <div className="h-[env(safe-area-inset-bottom)] md:hidden" />
    </nav>
  );
}

export default BottomNav;
